import React from "react";

interface State {
  err: Error | null;
  info: string | null;
}

/**
 * Catches render errors anywhere below. Without this, a thrown error
 * (e.g. an infinite render loop bailing out) unmounts the tree and leaves
 * the viewport black.
 */
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { err: null, info: null };

  static getDerivedStateFromError(err: Error): State {
    return { err, info: null };
  }

  componentDidCatch(err: Error, info: React.ErrorInfo) {
    this.setState({ err, info: info.componentStack ?? null });
    console.error("[ErrorBoundary]", err, info);
  }

  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div className="min-h-screen bg-bg text-text p-10 overflow-auto">
        <div className="max-w-3xl mx-auto">
          <div className="text-[11px] tracking-[0.22em] uppercase text-wb mb-2">
            Frontend error
          </div>
          <h1 className="text-2xl font-semibold mb-6">{this.state.err.message}</h1>
          <button
            className="mb-6 px-3 py-1.5 border border-border2 hover:border-wb text-muted hover:text-wb text-[12px] uppercase tracking-widest rounded"
            onClick={() => this.setState({ err: null, info: null })}
          >
            Try again
          </button>
          <pre className="text-[11px] text-muted whitespace-pre-wrap break-words bg-surface1 border border-border1 rounded p-4">
            {this.state.err.stack}
            {this.state.info ? `\n\nComponent stack:\n${this.state.info}` : null}
          </pre>
        </div>
      </div>
    );
  }
}
